import React, { useEffect, useState } from 'react';
import { 
  Box, 
  Container,  
  Typography, 
  Table, 
  TableBody, 
  TableCell, 
  TableContainer, 
  TableHead, 
  TableRow, 
  Paper, 
  Dialog, 
  DialogTitle, 
  DialogContent, 
  DialogActions, 
  Button, 
  TextField,
  MenuItem,
  Chip,
  IconButton,
  Tooltip,
  Divider,
  useTheme,
  useMediaQuery,
  Skeleton,
} from '@mui/material';
import { FilterList } from '@mui/icons-material';
import { Autocomplete } from '@mui/material';
import { useNavbar } from '../context/NavbarContext';
import { 
  Security as SecurityIcon,
  Edit as EditIcon,
  CheckCircle as CheckCircleIcon,
  Cancel as CancelIcon,
  Warning as WarningIcon
} from '@mui/icons-material';
import { useSelector } from 'react-redux';
import { getAllUsers, updateUserSitesAssignment, getAllSites, logActivity } from '../api/apiService';
import { styled, alpha } from '@mui/material/styles';

// Styled components
const StyledPaper = styled(Paper)(({ theme }) => ({
  borderRadius: "12px",
  overflow: 'hidden',
  boxShadow: theme.palette.mode === 'dark' ? '0 6px 18px rgba(0,0,0,0.35)' : '0 4px 10px rgba(0,0,0,0.05)',
  backgroundColor: theme.palette.background.paper,
  border: `1px solid ${theme.palette.divider}`,
  '& .MuiTableHead-root': {
    backgroundColor: theme.palette.mode === 'dark' ? alpha(theme.palette.background.paper, 0.5) : theme.palette.grey[50],
  },
  '& .MuiTableRow-root:hover': {
    backgroundColor: alpha(theme.palette.brand?.royalBlue || theme.palette.primary.main, theme.palette.mode === 'dark' ? 0.10 : 0.06),
  }
}));

const StyledTableCell = styled(TableCell)(({ theme }) => ({
  padding: theme.spacing(2),
  fontSize: "14px",
  color: theme.palette.text.primary,
  borderBottom: `1px solid ${theme.palette.divider}`,
  '&.MuiTableCell-head': {
    fontWeight: 600,
    color: theme.palette.text.secondary,
    backgroundColor: theme.palette.mode === 'dark' ? alpha(theme.palette.background.paper, 0.5) : theme.palette.grey[50],
  }
}));

const StyledAutocomplete = styled(Autocomplete)(({ theme }) => ({
  '& .MuiOutlinedInput-root': {
    backgroundColor: theme.palette.background.paper,
    borderRadius: "8px",
    '&:hover .MuiOutlinedInput-notchedOutline': {
      borderColor: theme.palette.brand?.royalBlue || theme.palette.primary.main,
    },
    '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
      borderColor: theme.palette.brand?.royalBlue || theme.palette.primary.main,
    },
  },
  '& .MuiOutlinedInput-notchedOutline': {
    borderColor: theme.palette.divider,
  }
}));

const UserPermissions = () => {
  const { isNavbarOpen } = useNavbar();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [users, setUsers] = useState([]);
  const [sites, setSites] = useState([]);
  const [selectedSites, setSelectedSites] = useState([]);
  const [selectedUser, setSelectedUser] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [roleFilter, setRoleFilter] = useState('All');
  const [siteFilter, setSiteFilter] = useState('All');
  const [sortField, setSortField] = useState('email');
  const [sortOrder, setSortOrder] = useState('asc');
  const [openDialog, setOpenDialog] = useState(false);
  const [loadingUsers, setLoadingUsers] = useState(true);
  const [loadingSites, setLoadingSites] = useState(true);
  const [openFilterDialog, setOpenFilterDialog] = useState(false);
  const currentUser = useSelector((state) => state.auth.user);
  const canEditPermissions = currentUser?.role === 'Admin';

  // Compute filtered and sorted users
  const filteredUsers = users
    .filter((u) =>
      u.email.toLowerCase().includes(searchQuery.toLowerCase())
    )
    .filter((u) =>
      roleFilter === 'All' || (u.role || 'User') === roleFilter
    )
    .filter((u) =>
      siteFilter === 'All' ||
      u.sites?.some((s) => s.id === siteFilter)
    )
    .sort((a, b) => {
      let aVal, bVal;
      switch (sortField) {
        case 'role':
          aVal = a.role || 'User'; bVal = b.role || 'User'; break;
        case 'sites':
          aVal = (a.sites?.[0]?.id) || ''; bVal = (b.sites?.[0]?.id) || ''; break;
        default:
          aVal = a.email; bVal = b.email;
      }
      if (aVal < bVal) return sortOrder === 'asc' ? -1 : 1;
      if (aVal > bVal) return sortOrder === 'asc' ? 1 : -1;
      return 0;
    });

  // console.log("Fetched Current site:", currentUser.email);

  useEffect(() => {
    const fetchUsers = async () => {
      try {
        setLoadingUsers(true);
        console.log('[UserPermissions] Fetching users...');
        const data = await getAllUsers();
        console.log('[UserPermissions] Fetched users:', data);
        
        // Ensure data is an array
        if (Array.isArray(data)) {
          setUsers(data);
        } else {
          console.error('[UserPermissions] Fetched data is not an array:', data);
          setUsers([]);
        }
      } catch (error) {
        console.error('[UserPermissions] Error fetching users:', error);
        setUsers([]); // Ensure users is always an array
      } finally {
        setLoadingUsers(false);
      }
    };

    fetchUsers();
  }, []);

  useEffect(() => {
    const getSites = async () => {
      try {
        setLoadingSites(true);
        console.log('[UserPermissions] Fetching sites...');
        const fetchedSites = await getAllSites();
        console.log('[UserPermissions] Fetched sites:', fetchedSites);
        
        // Ensure fetchedSites is an array
        if (Array.isArray(fetchedSites)) {
          setSites(fetchedSites);
        } else {
          console.error('[UserPermissions] Fetched data is not an array:', fetchedSites);
          setSites([]);
        }
      } catch (error) {
        console.error('[UserPermissions] Error fetching sites:', error);
        setSites([]); // Ensure sites is always an array
      } finally {
        setLoadingSites(false);
      }
    };

    getSites();
  }, []);

  const handleSitesChange = (userId, selectedOptions) => {
    if (!canEditPermissions) return;
    setSelectedUser(userId);
    setSelectedSites(selectedOptions || []);
    setOpenDialog(true);
  };

  const handleConfirm = async () => {
    if (!canEditPermissions) {
      setOpenDialog(false);
      return;
    }
    if (selectedUser) {
      try {
        const sitesArray = selectedSites.map((s) => ({ name: s.client, id: s.id }));
        console.log('Updating user sites:', { userEmail: selectedUser, sitesArray });
        await updateUserSitesAssignment(selectedUser, sitesArray);
        await logActivity(
          currentUser.email,
          `Assigned sites: ${sitesArray.map((s) => s.id).join(', ')} to ${selectedUser}`
        );
        console.log(
          `Successfully assigned sites ${sitesArray.map((s) => s.name).join(', ')} to user ${selectedUser}`
        );
        // Refresh the users list
        const updatedUsers = await getAllUsers();
        setUsers(updatedUsers);
      } catch (error) {
        console.error('Error updating sites:', error);
      }
    }
    setOpenDialog(false);
  };

  const handleCancel = () => {
    setOpenDialog(false);
  };

  const getRoleChip = (role) => {
    const roleConfig = {
      'User': { color: 'primary', icon: <CheckCircleIcon /> },
      'Admin': { color: 'success', icon: <SecurityIcon /> },
      'Pending': { color: 'warning', icon: <WarningIcon /> }
    };

    const config = roleConfig[role] || { color: 'default', icon: null };
    return (
      <Chip
        icon={config.icon}
        label={role}
        color={config.color}
        size="small"
        sx={{ 
          borderRadius: '4px',
          '& .MuiChip-icon': { 
            fontSize: {
              xs: '14px',
              sm: '16px',
              md: '16px'
            }
          },
          '& .MuiChip-label': { 
            fontSize: {
              xs: '10px',
              sm: '12px',
              md: '12px'
            },
            fontWeight: 500 
          }
        }}
      />
    );
  };

  return (
    <Box sx={{ 
      display: 'flex', 
      minHeight: '100vh', 
      width: '100%', 
      background: (theme) => theme.palette.background.default,
      margin: 0,
      padding: 0,
      overflow: 'auto',
    }}>
      <Container
        disableGutters
        sx={{
          minHeight: '100vh',
          width: '100%',
          flexGrow: 1,
          padding: {
            xs: '12px',
            sm: '16px',
            md: '20px',
            lg: '24px',
            xl: '32px'
          },
          marginLeft: {
            xs: isNavbarOpen ? '240px' : '70px',
            sm: isNavbarOpen ? '240px' : '70px',
            md: isNavbarOpen ? '240px' : '70px',
            lg: isNavbarOpen ? '240px' : '70px',
            xl: isNavbarOpen ? '240px' : '70px'
          },
          marginTop: {
            xs: '65px',
            sm: '64px',
            md: '64px',
            lg: '64px',
            xl: '64px'
          },
          transition: 'margin-left 0.3s, padding 0.3s',
          position: 'relative',
          maxWidth: '100% !important',
          overflow: 'auto',
        }}
      >
        {/* Page Header */}
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5, mb: { xs: 2, sm: 3, md: 3 } }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <SecurityIcon sx={{ color: (theme) => theme.palette.brand?.royalBlue || theme.palette.primary.main, fontSize: { xs: 24, sm: 28, md: 32 } }} />
            <Typography 
              variant="h4" 
              fontWeight="600" 
              sx={{ 
                color: (theme) => theme.palette.text.primary,
                letterSpacing: '-0.5px',
                fontSize: { xs: '1.5rem', sm: '1.75rem', md: '2rem', lg: '2.25rem', xl: '2.5rem' }
              }}
            >
              User Permissions
            </Typography>
          </Box>
          <Typography variant="body2" sx={{ color: (theme) => theme.palette.text.secondary, ml: { xs: 0, sm: 5 } }}>
            {canEditPermissions
              ? 'Manage user roles and site assignments.'
              : 'View-only: browse site assignments. Changes require Admin access.'}
          </Typography>
          {isMobile && (
            <Box sx={{ mt: 1, display: 'flex', gap: 1 }}>
              <TextField
                label="Search User ID"
                aria-label="Search User ID"
                variant="outlined"
                size='small'
                fullWidth
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    backgroundColor: (theme) => theme.palette.background.paper,
                    '&:hover fieldset': {
                      borderColor: (theme) => theme.palette.brand?.royalBlue || theme.palette.primary.main,
                    },
                    '&.Mui-focused fieldset': {
                      borderColor: (theme) => theme.palette.brand?.royalBlue || theme.palette.primary.main,
                    },
                  },
                  '& .MuiInputLabel-root.Mui-focused': {
                    color: (theme) => theme.palette.brand?.royalBlue || theme.palette.primary.main,
                  },
                }}
              />
              <Button 
                variant="outlined" 
                startIcon={<FilterList />} 
                onClick={() => setOpenFilterDialog(true)}
                aria-label="Open filters"
                sx={{
                  borderColor: (theme) => theme.palette.brand?.royalBlue || theme.palette.primary.main,
                  color: (theme) => theme.palette.brand?.royalBlue || theme.palette.primary.main,
                  '&:hover': {
                    borderColor: (theme) => theme.palette.brand?.royalBlue || theme.palette.primary.main,
                    backgroundColor: (theme) => alpha(theme.palette.brand?.royalBlue || theme.palette.primary.main, theme.palette.mode === 'dark' ? 0.16 : 0.08),
                  },
                }}
              >
                Filters
              </Button>
            </Box>
          )}
        </Box>

        {/* Search and Filters - desktop/tablet only */}
        {!isMobile && (
        <Paper elevation={0} sx={{ mb: 2, p: { xs: 1.5, sm: 2 }, borderRadius: '12px', border: (theme) => `1px solid ${theme.palette.divider}`, backgroundColor: (theme) => theme.palette.background.paper, boxShadow: (theme) => theme.palette.mode === 'dark' ? '0 6px 18px rgba(0,0,0,0.35)' : '0 4px 10px rgba(0,0,0,0.05)' }}>
          {loadingSites ? (
            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)', md: 'repeat(4, 1fr)' }, gap: 2 }}>
              <Skeleton variant="rounded" height={48} />
              <Skeleton variant="rounded" height={48} />
              <Skeleton variant="rounded" height={48} />
              <Skeleton variant="rounded" height={48} />
            </Box>
          ) : (
          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)', md: 'repeat(4, 1fr)' }, gap: 2, width: '100%' }}>
            <TextField
              label="Search User ID"
              aria-label="Search User ID"
              variant="outlined"
              size={isMobile ? 'small' : 'medium'}
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              sx={{
                '& .MuiOutlinedInput-root': {
                  backgroundColor: (theme) => theme.palette.background.paper,
                  '&:hover fieldset': {
                    borderColor: (theme) => theme.palette.brand?.royalBlue || theme.palette.primary.main,
                  },
                  '&.Mui-focused fieldset': {
                    borderColor: (theme) => theme.palette.brand?.royalBlue || theme.palette.primary.main,
                  },
                },
                '& .MuiInputLabel-root.Mui-focused': {
                  color: (theme) => theme.palette.brand?.royalBlue || theme.palette.primary.main,
                },
              }}
            />
            <TextField
              select
              label="Filter Role"
              aria-label="Filter Role"
              value={roleFilter}
              size={isMobile ? 'small' : 'medium'}
              onChange={(e) => setRoleFilter(e.target.value)}
              sx={{
                '& .MuiOutlinedInput-root': {
                  backgroundColor: (theme) => theme.palette.background.paper,
                  '&:hover .MuiOutlinedInput-notchedOutline': {
                    borderColor: (theme) => theme.palette.brand?.royalBlue || theme.palette.primary.main,
                  },
                  '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                    borderColor: (theme) => theme.palette.brand?.royalBlue || theme.palette.primary.main,
                  },
                },
                '& .MuiInputLabel-root.Mui-focused': {
                  color: (theme) => theme.palette.brand?.royalBlue || theme.palette.primary.main,
                },
              }}
            >
              <MenuItem value="All">All Roles</MenuItem>
              <MenuItem value="Admin">Admin</MenuItem>
              <MenuItem value="User">User</MenuItem>
              <MenuItem value="Pending">Pending</MenuItem>
            </TextField>
            <TextField
              select
              label="Filter Site"
              aria-label="Filter Site"
              value={siteFilter}
              size={isMobile ? 'small' : 'medium'}
              onChange={(e) => setSiteFilter(e.target.value)}
              sx={{
                '& .MuiOutlinedInput-root': {
                  backgroundColor: (theme) => theme.palette.background.paper,
                  '&:hover .MuiOutlinedInput-notchedOutline': {
                    borderColor: (theme) => theme.palette.brand?.royalBlue || theme.palette.primary.main,
                  },
                  '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                    borderColor: (theme) => theme.palette.brand?.royalBlue || theme.palette.primary.main,
                  },
                },
                '& .MuiInputLabel-root.Mui-focused': {
                  color: (theme) => theme.palette.brand?.royalBlue || theme.palette.primary.main,
                },
              }}
            >
              <MenuItem value="All">All Sites</MenuItem>
              {sites.map((s) => (
                <MenuItem key={s.id} value={s.id}>{s.id}</MenuItem>
              ))}
            </TextField>
            <Box sx={{ display: 'flex', gap: 2 }}>
              <TextField
                select
                label="Sort By"
                aria-label="Sort By"
                value={sortField}
                size={isMobile ? 'small' : 'medium'}
                onChange={(e) => setSortField(e.target.value)}
                sx={{ 
                  flex: 1,
                  '& .MuiOutlinedInput-root': {
                    backgroundColor: (theme) => theme.palette.background.paper,
                    '&:hover .MuiOutlinedInput-notchedOutline': {
                      borderColor: (theme) => theme.palette.brand?.royalBlue || theme.palette.primary.main,
                    },
                    '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                      borderColor: (theme) => theme.palette.brand?.royalBlue || theme.palette.primary.main,
                    },
                  },
                  '& .MuiInputLabel-root.Mui-focused': {
                    color: (theme) => theme.palette.brand?.royalBlue || theme.palette.primary.main,
                  },
                }}
              >
                <MenuItem value="email">User ID</MenuItem>
                <MenuItem value="role">Role</MenuItem>
                <MenuItem value="sites">Assigned Sites</MenuItem>
              </TextField>
              <TextField
                select
                label="Order"
                aria-label="Sort Order"
                value={sortOrder}
                size={isMobile ? 'small' : 'medium'}
                onChange={(e) => setSortOrder(e.target.value)}
                sx={{ 
                  flex: 1,
                  '& .MuiOutlinedInput-root': {
                    backgroundColor: (theme) => theme.palette.background.paper,
                    '&:hover .MuiOutlinedInput-notchedOutline': {
                      borderColor: (theme) => theme.palette.brand?.royalBlue || theme.palette.primary.main,
                    },
                    '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                      borderColor: (theme) => theme.palette.brand?.royalBlue || theme.palette.primary.main,
                    },
                  },
                  '& .MuiInputLabel-root.Mui-focused': {
                    color: (theme) => theme.palette.brand?.royalBlue || theme.palette.primary.main,
                  },
                }}
              >
                <MenuItem value="asc">Ascending</MenuItem>
                <MenuItem value="desc">Descending</MenuItem>
              </TextField>
            </Box>
          </Box>
          )}
        </Paper>
        )}

        {/* Table Section */}
        <StyledPaper elevation={2}>
          {loadingUsers ? (
            <Box sx={{ p: 2 }}>
              <Skeleton variant="rectangular" height={40} sx={{ mb: 1 }} />
              {[...Array(6)].map((_, idx) => (
                <Skeleton key={idx} variant="rectangular" height={isMobile ? 36 : 52} sx={{ mb: 1 }} />
              ))}
            </Box>
          ) : (
          <TableContainer sx={{ overflowX: 'auto' }}>
            <Table size={isMobile ? "small" : "medium"} aria-label="User permissions table">
              <TableHead>
                <TableRow>
                  <StyledTableCell sx={{ 
                    fontSize: {
                      xs: '0.75rem',
                      sm: '0.8rem',
                      md: '0.875rem'
                    }
                  }}>
                    User ID
                  </StyledTableCell>
                  <StyledTableCell sx={{ 
                    fontSize: {
                      xs: '0.75rem',
                      sm: '0.8rem',
                      md: '0.875rem'
                    }
                  }}>
                    Role
                  </StyledTableCell>
                  <StyledTableCell sx={{ 
                    fontSize: {
                      xs: '0.75rem',
                      sm: '0.8rem',
                      md: '0.875rem'
                    }
                  }}>
                    Assigned Sites
                  </StyledTableCell>
                  {canEditPermissions && (
                  <StyledTableCell sx={{ 
                    fontSize: {
                      xs: '0.75rem',
                      sm: '0.8rem',
                      md: '0.875rem'
                    }
                  }}>
                    Actions
                  </StyledTableCell>
                  )}
                </TableRow>
              </TableHead>
              <TableBody>
                {filteredUsers.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={canEditPermissions ? 4 : 3} align="center">
                      <Typography variant="body2" sx={{ color: (theme) => theme.palette.text.secondary, py: 2 }}>No users match the current filters.</Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                filteredUsers.map((user) => (
                  <TableRow key={user.email} hover>
                    <StyledTableCell sx={{ 
                      fontSize: {
                        xs: '0.75rem',
                        sm: '0.8rem',
                        md: '0.875rem'
                      }
                    }}>
                      {user.email}
                    </StyledTableCell>
                    <StyledTableCell>{getRoleChip(user.role || 'User')}</StyledTableCell>
                    <StyledTableCell>
                      <StyledAutocomplete
                        multiple
                        disabled={!canEditPermissions}
                        readOnly={!canEditPermissions}
                        options={sites}
                        getOptionLabel={(option) => option.id || ''}
                        renderTags={(value, getTagProps) =>
                          value.map((option, index) => {
                            const { key, ...tagProps } = getTagProps({ index });
                            return (
                              <Chip
                                key={option.id}
                                label={option.id}
                                {...tagProps}
                                size="small"
                                // Keep chips visible in read-only; hide delete affordance for Manager
                                onDelete={canEditPermissions ? tagProps.onDelete : undefined}
                              />
                            );
                          })
                        }
                        renderInput={(params) => (
                          <TextField 
                            {...params} 
                            label={canEditPermissions ? 'Select Site IDs' : 'Assigned Site IDs'}
                            aria-label={canEditPermissions ? 'Select Site IDs' : 'Assigned Site IDs'}
                            variant="outlined"
                            size={isMobile ? 'small' : 'medium'}
                            sx={{
                              '& .MuiInputLabel-root': {
                                fontSize: {
                                  xs: '0.75rem',
                                  sm: '0.8rem',
                                  md: '0.875rem'
                                }
                              },
                              '& .MuiInputBase-root': {
                                fontSize: {
                                  xs: '0.75rem',
                                  sm: '0.8rem',
                                  md: '0.875rem'
                                }
                              }
                            }}
                          />
                        )}
                        value={
                          user.sites
                            ? sites.filter((s) => user.sites.some((us) => us.id === s.id))
                            : user.site && user.site.id
                            ? [sites.find((s) => s.id === user.site.id)].filter(Boolean)
                            : []
                        }
                        onChange={(event, value) => handleSitesChange(user.email, value)}
                      />
                    </StyledTableCell>
                    {canEditPermissions && (
                    <StyledTableCell>
                      <Tooltip title="Edit Permissions">
                        <IconButton 
                          size="small" 
                          aria-label={`Edit permissions for ${user.email}`}
                          onClick={() => handleSitesChange(user.email, user.sites)}
                          sx={{ 
                            color: (theme) => theme.palette.brand?.royalBlue || theme.palette.primary.main,
                            '&:hover': { backgroundColor: (theme) => alpha(theme.palette.brand?.royalBlue || theme.palette.primary.main, theme.palette.mode === 'dark' ? 0.16 : 0.10) },
                            '&:focus-visible': {
                              outline: (theme) => `2px solid ${theme.palette.brand?.royalBlue || theme.palette.primary.main}`,
                              outlineOffset: '2px',
                            },
                          }}
                        >
                          <EditIcon />
                        </IconButton>
                      </Tooltip>
                    </StyledTableCell>
                    )}
                  </TableRow>
                )))}
              </TableBody>
            </Table>
          </TableContainer>
          )}
        </StyledPaper>

        {/* Mobile Filters Dialog */}
        <Dialog open={openFilterDialog} onClose={() => setOpenFilterDialog(false)} fullWidth maxWidth="sm">
          <DialogTitle>Filters</DialogTitle>
          <DialogContent>
            {loadingSites ? (
              <Box sx={{ display: 'grid', gridTemplateColumns: '1fr', gap: 2, mt: 1 }}>
                <Skeleton variant="rounded" height={48} />
                <Skeleton variant="rounded" height={48} />
                <Skeleton variant="rounded" height={48} />
                <Skeleton variant="rounded" height={48} />
              </Box>
            ) : (
            <Box sx={{ display: 'grid', gridTemplateColumns: '1fr', gap: 2, mt: 1 }}>
              <TextField
                label="Search User ID"
                aria-label="Search User ID"
                variant="outlined"
                size='small'
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    backgroundColor: (theme) => theme.palette.background.paper,
                    '&:hover fieldset': {
                      borderColor: (theme) => theme.palette.brand?.royalBlue || theme.palette.primary.main,
                    },
                    '&.Mui-focused fieldset': {
                      borderColor: (theme) => theme.palette.brand?.royalBlue || theme.palette.primary.main,
                    },
                  },
                  '& .MuiInputLabel-root.Mui-focused': {
                    color: (theme) => theme.palette.brand?.royalBlue || theme.palette.primary.main,
                  },
                }}
              />
              <TextField
                select
                label="Filter Role"
                aria-label="Filter Role"
                value={roleFilter}
                size='small'
                onChange={(e) => setRoleFilter(e.target.value)}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    backgroundColor: (theme) => theme.palette.background.paper,
                    '&:hover .MuiOutlinedInput-notchedOutline': {
                      borderColor: (theme) => theme.palette.brand?.royalBlue || theme.palette.primary.main,
                    },
                    '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                      borderColor: (theme) => theme.palette.brand?.royalBlue || theme.palette.primary.main,
                    },
                  },
                  '& .MuiInputLabel-root.Mui-focused': {
                    color: (theme) => theme.palette.brand?.royalBlue || theme.palette.primary.main,
                  },
                }}
              >
                <MenuItem value="All">All Roles</MenuItem>
                <MenuItem value="Admin">Admin</MenuItem>
                <MenuItem value="User">User</MenuItem>
                <MenuItem value="Pending">Pending</MenuItem>
              </TextField>
              <TextField
                select
                label="Filter Site"
                aria-label="Filter Site"
                value={siteFilter}
                size='small'
                onChange={(e) => setSiteFilter(e.target.value)}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    backgroundColor: (theme) => theme.palette.background.paper,
                    '&:hover .MuiOutlinedInput-notchedOutline': {
                      borderColor: (theme) => theme.palette.brand?.royalBlue || theme.palette.primary.main,
                    },
                    '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                      borderColor: (theme) => theme.palette.brand?.royalBlue || theme.palette.primary.main,
                    },
                  },
                  '& .MuiInputLabel-root.Mui-focused': {
                    color: (theme) => theme.palette.brand?.royalBlue || theme.palette.primary.main,
                  },
                }}
              >
                <MenuItem value="All">All Sites</MenuItem>
                {sites.map((s) => (
                  <MenuItem key={s.id} value={s.id}>{s.id}</MenuItem>
                ))}
              </TextField>
              <TextField
                select
                label="Sort By"
                aria-label="Sort By"
                value={sortField}
                size='small'
                onChange={(e) => setSortField(e.target.value)}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    backgroundColor: (theme) => theme.palette.background.paper,
                    '&:hover .MuiOutlinedInput-notchedOutline': {
                      borderColor: (theme) => theme.palette.brand?.royalBlue || theme.palette.primary.main,
                    },
                    '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                      borderColor: (theme) => theme.palette.brand?.royalBlue || theme.palette.primary.main,
                    },
                  },
                  '& .MuiInputLabel-root.Mui-focused': {
                    color: (theme) => theme.palette.brand?.royalBlue || theme.palette.primary.main,
                  },
                }}
              >
                <MenuItem value="email">User ID</MenuItem>
                <MenuItem value="role">Role</MenuItem>
                <MenuItem value="sites">Assigned Sites</MenuItem>
              </TextField>
              <TextField
                select
                label="Order"
                aria-label="Sort Order"
                value={sortOrder}
                size='small'
                onChange={(e) => setSortOrder(e.target.value)}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    backgroundColor: (theme) => theme.palette.background.paper,
                    '&:hover .MuiOutlinedInput-notchedOutline': {
                      borderColor: (theme) => theme.palette.brand?.royalBlue || theme.palette.primary.main,
                    },
                    '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                      borderColor: (theme) => theme.palette.brand?.royalBlue || theme.palette.primary.main,
                    },
                  },
                  '& .MuiInputLabel-root.Mui-focused': {
                    color: (theme) => theme.palette.brand?.royalBlue || theme.palette.primary.main,
                  },
                }}
              >
                <MenuItem value="asc">Ascending</MenuItem>
                <MenuItem value="desc">Descending</MenuItem>
              </TextField>
            </Box>
            )}
          </DialogContent>
          <DialogActions>
            <Button 
              onClick={() => setOpenFilterDialog(false)}
              sx={{
                color: (theme) => theme.palette.text.secondary,
                '&:hover': {
                  backgroundColor: (theme) => alpha(theme.palette.text.secondary, theme.palette.mode === 'dark' ? 0.12 : 0.08),
                },
              }}
            >
              Close
            </Button>
            <Button 
              onClick={() => setOpenFilterDialog(false)} 
              variant="contained"
              sx={{
                backgroundColor: (theme) => theme.palette.brand?.royalBlue || theme.palette.primary.main,
                color: '#FFFFFF',
                '&:hover': {
                  backgroundColor: (theme) => theme.palette.mode === 'dark'
                    ? alpha(theme.palette.brand?.royalBlue || theme.palette.primary.main, 0.85)
                    : alpha(theme.palette.brand?.royalBlue || theme.palette.primary.main, 0.90),
                },
              }}
            >
              Apply
            </Button>
          </DialogActions>
        </Dialog>

        {/* Confirmation Dialog */}
        <Dialog 
          open={openDialog} 
          onClose={handleCancel}
          PaperProps={{
            sx: {
              borderRadius: '12px',
              padding: '16px',
              width: {
                xs: '90%',
                sm: '400px',
                md: '500px'
              },
              m: 2
            }
          }}
        >
          <DialogTitle sx={{ 
            display: 'flex', 
            alignItems: 'center', 
            gap: 1,
            color: (theme) => theme.palette.text.primary,
            fontWeight: 600,
            fontSize: {
              xs: '1rem',
              sm: '1.25rem',
              md: '1.5rem'
            }
          }}>
            <SecurityIcon sx={{ color: (theme) => theme.palette.brand?.royalBlue || theme.palette.primary.main }} />
            Confirm Site Assignments
          </DialogTitle>
          <Divider sx={{ my: 2 }} />
          <DialogContent>
            <Typography sx={{ 
              color: (theme) => theme.palette.text.primary,
              fontSize: {
                xs: '0.75rem',
                sm: '0.8rem',
                md: '0.875rem'
              }
            }}>
              Are you sure you want to assign these sites to the user?
            </Typography>
            <Typography variant="body2" sx={{ mt: 1 }}>
              {selectedSites.map((site) => site.client).join(', ')}
            </Typography>
          </DialogContent>
          <DialogActions sx={{ px: 3, pb: 3 }}>
            <Button 
              onClick={handleCancel} 
              startIcon={<CancelIcon />}
              sx={{ 
                color: (theme) => theme.palette.text.secondary,
                '&:hover': { backgroundColor: (theme) => theme.palette.mode === 'dark' ? alpha('#FFFFFF', 0.06) : 'rgba(102, 102, 102, 0.04)' },
                fontSize: {
                  xs: '0.75rem',
                  sm: '0.8rem',
                  md: '0.875rem'
                }
              }}
            >
              Cancel
            </Button>
            <Button 
              onClick={handleConfirm} 
              variant="contained"
              startIcon={<CheckCircleIcon />}
              sx={{ 
                backgroundColor: (theme) => theme.palette.brand?.royalBlue || theme.palette.primary.main,
                color: '#FFFFFF',
                '&:hover': { 
                  backgroundColor: (theme) => theme.palette.mode === 'dark'
                    ? alpha(theme.palette.brand?.royalBlue || theme.palette.primary.main, 0.85)
                    : alpha(theme.palette.brand?.royalBlue || theme.palette.primary.main, 0.90)
                },
                fontSize: {
                  xs: '0.75rem',
                  sm: '0.8rem',
                  md: '0.875rem'
                }
              }}
            >
              Confirm
            </Button>
          </DialogActions>
        </Dialog>
      </Container>
    </Box>
  );
};

export default UserPermissions;
